import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Divider,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  ThemeProvider,
  createTheme,
  TextField,
  Button as MuiButton,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  Home as HomeIcon,
  Users as UsersIcon,
  Store,
  ColumnsThree,
  Notification,
  Analysis,
  Taxonomy,
  Edit,
  Warning,
  ChartLine,
  ChartBar,
  Publisher,
  Select,
  Upload,
  Speedometor,
  Settings,
  Kitchen,
  History,
  InternalUser,
  Video,
  InfoCircle,
  App,
} from "lift-ui";
import { Sparkles, RefreshCw, FileText } from "lucide-react";
import { IconMailFast } from "@tabler/icons-react";
import "./home.styles.scss";

// Lift UI Sidebar Components
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { NavMain, NavChannel } from "@/components/nav-main";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Topbar } from "@/components/Topbar";

// Feature Components
import CRMUserAnalytics from "../features/crm-user-analytics/crm-user-analytics.component";
import Users from "../features/users/users.component";
import Brands from "../features/brand/components/brands/brands.component";
import BrandWeights from "../features/brand/components/brand-weights/brand-weights.component";
import NotificationsTeamsComponent from "../features/notification-teams";
import { useAppDispatch, useAppSelector } from "../shared/redux/hooks";
import { logout_admin } from "../shared/redux/action/auth.action";

import { Toastify } from 'lift-ui';
import { logEvent } from "firebase/analytics";
import { getFirebaseAnalytics, setLoggingOut } from "@lift-admin/utils";
import OverView from "../features/overview/overview.component";
import PubMapping from "./pub-map";
import MatrixComponent from "./matrix";
import ImportSection from "./imports/import-sections";
import { actionLogTrack } from "@lift-admin/utils/actionLogTrack";
import OrganizationDown from "./organization-down/org-down.component";
import { usePostHog } from "posthog-js/react";
import { actionLog } from "../App";
import PerfAccounts from "./performance-accounts/PerfAccounts";

import { useFlag, useUnleashClient } from "@unleash/proxy-client-react";
import RequestListComponent from "./request-list";
import { useAuthContext } from "@lift-admin/utils/provider";
import Trigger from "./trigger";
import TaxonomyMergeComponent from "./taxonomy/TaxonomyMerge";
import DrawerStructure from "./drawer-structure";
import DrawerStructureV2 from "./drawer-structure-v2";
import TaxonomyName from "./taxonomy/components/taxonomy-name/taxonomy-name.component";
import ChatBotV2 from "./chatbot-v2";
import ClaudeCodeDashboard from "./claude-code-dashboard";
import IntegrationTestTrigger from "./trigger/integration-test";
import PlayWrightTrigger from "./trigger/playwright-trigger";
import AdverityLogs from "./adverity-logs";
import OnboardingLogs from "./onboarding-logs";
import BrandParamsMO from "./brand-params-mo/brand-params-mo.component";
import PitchingToolOperationRunningTable from "./pitching-tool";
import UserAnalyticsV2 from "./user-analytics-v2";
import EngenExceptions from "./engen-exceptions";
import PivotContentKitchen from "./pivot-content-kitchen";
import BuddyConfig from "../features/buddy-config";
import PacingLookerDashboard from "./pacing-looker-dashboard/pacing-looker-dashboard.component";
import IAMSupport from "./iam-support";
import GA4Config from "./ga4-config";
import BigQueryMetadata from "../features/bigquery-metadata";
import LiftAIModels from "../features/lift-ai-models";
import ReportConfiguration from "./report-configuration";
import { DMGAlertSystem } from "./dmg-alert-system";
import { Database, Brain, FileBarChart, BellRing, Plug, Workflow } from "lucide-react";
import NotFoundView from "../components/NotFoundView";
import MotionIntegration from "../features/motion-integration";
import GrapevineJobs from "../features/grapevine-jobs";

const theme = createTheme({
  palette: {
    primary: {
      main: "#618C70",
    },
  },
  typography: {
    fontFamily: '"Figtree", sans-serif',
  },
});

// Define navigation channels with their items (General structure mirrors lift-ui teams sidebar: direct items + collapsible groupings with sub-items)
const getNavigationChannels = (
  isSuperAdmin: boolean,
  canViewGrapevineJobs: boolean
): NavChannel[] => [
  {
    id: "general",
    name: "General",
    displayOrder: 0,
    items: [
      { id: "overview", name: "Overview", icon: <HomeIcon />, clickEventName: "overview" },
      { id: "users-internal", name: "Users", icon: <UsersIcon />, clickEventName: "users-internal" },
      { id: "organization-down", name: "Organizations", icon: <App />, clickEventName: "organization-down" },
      { id: "brands", name: "Brands", icon: <Store />, clickEventName: "brands" },
      { id: "drawer-structure-v2", name: "Drawer Structure", icon: <ColumnsThree />, clickEventName: "drawer-structure-v2" },
      { id: "notification-teams", name: "Notification Teams", icon: <Notification />, clickEventName: "notification-teams" },
    ],
    groupings: [
      {
        id: "lift-analytics",
        name: "LIFT Analytics",
        icon: <Analysis />,
        displayOrder: 0,
        items: [
          { id: "user-analytics-v2", name: "LIFT User Analytics", icon: <HomeIcon />, clickEventName: "user-analytics-v2" },
          { id: "lift-ai-v2", name: "LIFT AI Dashboard", icon: <Sparkles />, clickEventName: "lift-ai-v2" },
          { id: "claude-code", name: "Claude Code Dashboard", icon: <Sparkles />, clickEventName: "claude-code" },
        ],
      },
    ],
  },
  {
    id: "affiliate",
    name: "Affiliate",
    displayOrder: 1,
    items: [
      { id: "crm-user-analytics", name: "CRM User Analytics", icon: <ChartLine />, clickEventName: "crm-user-analytics" },
      { id: "br-wt", name: "Brand Weights", icon: <ChartBar />, clickEventName: "br-wt" },
      { id: "pub-map", name: "Pub Mapping", icon: <Publisher />, clickEventName: "pub-map" },
      { id: "goal-matrix", name: "Goal Matrix", icon: <Select />, clickEventName: "goal-matrix" },
      { id: "pitching-tool", name: "Pitching Tool Dashboard", icon: <IconMailFast size={18} />, clickEventName: "pitching-tool" },
      { id: "ga4-config", name: "GA4 Configuration", icon: <ChartLine />, clickEventName: "ga4-config" },
      { id: "imports", name: "Affluent Imports", icon: <Upload />, clickEventName: "imports" },
    ],
    groupings: [],
  },
  {
    id: "performance",
    name: "Performance",
    displayOrder: 2,
    items: [
      { id: "performance", name: "Media Platform Accounts", icon: <Store />, clickEventName: "performance" },
      { id: "report-configuration", name: "Report Configuration", icon: <FileBarChart size={18} />, clickEventName: "report-configuration" },
      { id: "adverity-logs", name: "Adverity Logs", icon: <FileText />, clickEventName: "adverity-logs" },
      { id: "onboarding-logs", name: "Onboarding Logs", icon: <FileText />, clickEventName: "onboarding-logs" },
      { id: "pacing-looker-dashboard", name: "Pacing Rollup Dashboard", icon: <Speedometor />, clickEventName: "pacing-looker-dashboard" },
    ],
    groupings: [
      {
        id: "taxonomy",
        name: "Taxonomy",
        icon: <Taxonomy />,
        displayOrder: 0,
        items: [
          { id: "trigger", name: "Taxonomy Import Triggers", icon: <RefreshCw />, clickEventName: "trigger" },
          { id: "taxonomy-merge", name: "Merge Taxonomy Column", icon: <Taxonomy />, clickEventName: "taxonomy-merge" },
          { id: "taxonomy-name", name: "Naming Taxonomy", icon: <Edit />, clickEventName: "taxonomy-name" },
          { id: "engen-exceptions", name: "Engen Exception", icon: <Warning />, clickEventName: "engen-exceptions" },
        ],
      },
    ],
  },
  {
    id: "creative",
    name: "Creative",
    displayOrder: 3,
    items: [
      { id: "brand-params-mo", name: "Brand Info MO", icon: <Settings />, clickEventName: "brand-params-mo" },
      { id: "pivot-content-kitchen", name: "Kitchen Buddies", icon: <Kitchen />, clickEventName: "pivot-content-kitchen" },
      { id: "buddy-config", name: "Buddy Configuration", icon: <Settings />, clickEventName: "buddy-config" },
    ],
    groupings: [],
  },
  // Hidden without Viewer access to grapevine; the gateway enforces the
  // same grant on every request the channel makes.
  ...(canViewGrapevineJobs
    ? [
        {
          id: "grapevine",
          name: "Grapevine",
          displayOrder: 4,
          items: [
            {
              id: "grapevine-jobs",
              name: "Grapevine Jobs",
              icon: <Workflow size={18} />,
              clickEventName: "grapevine-jobs",
            },
            {
              id: "grapevine-creators",
              name: "Grapevine Creators",
              icon: <UsersIcon />,
              clickEventName: "grapevine-creators",
            },
          ],
          groupings: [],
        },
      ]
    : []),
    {
    id: "admin-settings",
    name: "Admin/ Settings",
    displayOrder: 5,
    items: [
      { id: "request-list", name: "Requests List", icon: <History />, clickEventName: "request-list" },
      ...((import.meta as any).env?.VITE_ENVIRONMENT === "development" ? [{ id: "bigquery-metadata", name: "BigQuery Metadata", icon: <Database size={18} />, clickEventName: "bigquery-metadata" }] : []),
      ...(isSuperAdmin ? [{ id: "iam-support", name: "IAM Support", icon: <InternalUser />, clickEventName: "iam-support" }] : []),
      { id: "playwright-trigger", name: "Triggers", icon: <Video />, clickEventName: "playwright-trigger" },
      ...(isSuperAdmin ? [{ id: "lift-ai-models", name: "Lift AI Models", icon: <Brain size={18} />, clickEventName: "lift-ai-models" }] : []),
      ...(isSuperAdmin ? [{ id: "motion-integration", name: "Motion Integration", icon: <Plug size={18} />, clickEventName: "motion-integration" }] : []),
      { id: "dmg-alerts", name: "DMG Alerts", icon: <BellRing size={18} />, clickEventName: "dmg-alerts" },
    ],
    groupings: [],
  },
];

// Sidebar Content Component
const SidebarContentComponent = ({
  channels,
  container,
  selectedOption,
  setContainer,
  setSelectedOption,
  superAdmin,
  super_admin,
}: {
  channels: NavChannel[];
  container: string;
  selectedOption: string;
  setContainer: (container: string) => void;
  setSelectedOption: (option: string) => void;
  superAdmin: boolean;
  super_admin: any;
}) => {
  useSidebar();

  return (
    <>
      {/* Navigation Content */}
      <SidebarContent className="thin-scrollbar px-2 py-3">
        <NavMain
          channels={channels}
          selectedContainer={container}
          selectedOption={selectedOption}
          onContainerChange={setContainer}
          onOptionChange={setSelectedOption}
          hasTeamsAccess={false}
        />
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter
        className="mt-auto flex flex-col w-full pt-2 px-3 pb-4 group-data-[collapsible=icon]:pt-1 group-data-[collapsible=icon]:px-3 group-data-[collapsible=icon]:pb-2 border-t border-sidebar-border"
        style={{ backgroundColor: "#F7F4F2" }}
      >
        {/* Expanded: Client portal + toggle (same layout as lift-teams "Admin portal" row) */}
        <div className="w-full flex items-center gap-2 group-data-[collapsible=icon]:hidden">
          <button
            onClick={() => window.open(import.meta.env.VITE_LIFT_URL ?? "https://lift.newengen.com", "_blank")}
            className="flex justify-center items-center h-[34px] bg-[#E5E1DF] rounded-md gap-1 hover:bg-[#D5D1CF] transition-colors flex-1"
          >
            <span className="text-sm font-semibold text-[#242424]">Client portal</span>
          </button>
          <SidebarTrigger className="w-8 h-8 rounded hover:bg-muted/20 text-foreground flex-shrink-0" />
        </div>

        {/* Collapsed: Toggle only */}
        <div className="hidden group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:gap-2">
          <SidebarTrigger className="w-6 h-6 rounded hover:bg-muted/20 text-foreground" />
        </div>
      </SidebarFooter>
    </>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { section } = useParams<{ section: string }>();
  const { admin_auth, superAdmin, admin_profile } = useAppSelector(
    (state) => state.admin
  );

  const user = JSON.parse(localStorage.getItem("user") as string);
  const super_admin = localStorage.getItem("super_admin")
    ? JSON.parse(localStorage.getItem("super_admin") || "")
    : null;

  // Use URL parameter as the source of truth for the current section
  const container = section || "overview";
  const [selectedOption, setSelectedOption] = useState("General");
  const posthog = usePostHog();
  const dispatch = useAppDispatch();

  // Navigate to the section route instead of setting state
  const setContainer = useCallback((newContainer: string) => {
    navigate(`/${newContainer}`);
  }, [navigate]);

  const { isSuperAdmin, canView } = useAuthContext();
  const canViewGrapevineJobs = canView("grapevine");
  const channels = getNavigationChannels(isSuperAdmin, canViewGrapevineJobs);

  // Check if integration test should be shown
  const showIntegrationTest = process.env.VITE_ENVIRONMENT !== "production";
  
  // Add integration test to General channel if needed
  React.useEffect(() => {
    if (showIntegrationTest) {
      const generalChannel = channels.find(c => c.id === "general");
      if (generalChannel && !generalChannel.items.find(i => i.id === "integration-test")) {
        generalChannel.items.push({
          id: "integration-test",
          name: "Integration Test Trigger",
          icon: <InfoCircle />,
          clickEventName: "integration-test",
        });
      }
    }
  }, [showIntegrationTest]);

  useEffect(() => {
    // Only redirect to overview if no section is specified in the URL
    if (!section) {
      navigate("/overview", { replace: true });
    }
  }, [superAdmin, section, navigate]);

  // Track home page visit with Firebase Analytics
  useEffect(() => {
    const trackPageVisit = async () => {
      try {
        const analytics = await getFirebaseAnalytics();
        if (analytics) {
          logEvent(analytics, "home_page_visited");
        }
      } catch (error) {
        console.warn("Failed to log home page visit:", error);
      }
    };
    trackPageVisit();
  }, []);

  const { handleLogout: handleLogout2 } = useAuthContext();
  const handleLogOut = async () => {
    setLoggingOut(true);
    handleLogout2();
    const desc = {
      message: `User with email ${user?.email} logged out`,
      status: 200,
      method: "",
      url: "",
    };

    await actionLog?.actionLogTrack(JSON.stringify(desc), false);

    if (superAdmin) {
      setContainer("");
    }
    navigate("/", {
      state: {
        email: null,
        password: null,
        action: "login",
      },
    });
    dispatch(logout_admin());
    Toastify("success", "Logout Successfully");
    localStorage.clear();
    posthog.reset(true);
  };

  const unleashClient = useUnleashClient();
  const newToolEnabled = useFlag("NewComponentAdmin");

  useEffect(() => {
    unleashClient.start();

    unleashClient.on("ready", () => {
      const enabledImpression = unleashClient.isEnabled("pitchingtoolUI");
      console.log(enabledImpression, "enabledIMPP");
    });

    unleashClient.on("impression", (impressionEvent: object) => {
      console.log(impressionEvent);
    });
  }, [unleashClient]);

  const renderContent = () => {
    switch (container) {
      case "overview":
        return <OverView setContainer={setContainer} />;
      case "user-analytics-v2":
        return <UserAnalyticsV2 />;
      case "crm-user-analytics":
        return <CRMUserAnalytics />;
      case "users-internal":
        return <Users key="users" signupSource="grapevine_creator" match="exclude" />;
      case "brands":
        return <Brands drawerOpen={true} />;
      case "br-wt":
        return <BrandWeights />;
      case "pub-map":
        return <PubMapping />;
      case "goal-matrix":
        return <MatrixComponent />;
      case "organization-down":
        return <OrganizationDown />;
      case "performance":
        return <PerfAccounts />;
      case "report-configuration":
        return <ReportConfiguration />;
      case "pacing-looker-dashboard":
        return <PacingLookerDashboard />;
      case "trigger":
        return <Trigger />;
      case "playwright-trigger":
        return <PlayWrightTrigger />;
      case "notification-teams":
        return <NotificationsTeamsComponent />;
      case "integration-test":
        return <IntegrationTestTrigger />;
      case "pitching-tool":
        return <PitchingToolOperationRunningTable />;
      case "taxonomy-merge":
        return <TaxonomyMergeComponent />;
      case "taxonomy-name":
        return <TaxonomyName />;
      case "engen-exceptions":
        return <EngenExceptions />;
      case "lift-ai-v2":
        return <ChatBotV2 />;
      case "claude-code":
        return <ClaudeCodeDashboard />;
      case "imports":
        return <ImportSection />;
      case "request-list":
        return <RequestListComponent />;
      case "drawer-structure":
        return <DrawerStructure />;
      case "drawer-structure-v2":
        return <DrawerStructureV2 />;
      case "adverity-logs":
        return <AdverityLogs />;

      case "onboarding-logs":
        return <OnboardingLogs />;

      case "brand-params-mo":
        return <BrandParamsMO />;
      case "pivot-content-kitchen":
        return <PivotContentKitchen />;
      case "buddy-config":
        return <BuddyConfig />;
      case "iam-support":
        return isSuperAdmin ? <IAMSupport /> : <NotFoundView />;
      case "ga4-config":
        return <GA4Config />;
      case "bigquery-metadata":
        return <BigQueryMetadata />;
      case "lift-ai-models":
        return <LiftAIModels />;
      case "motion-integration":
        return isSuperAdmin ? <MotionIntegration /> : <NotFoundView />;
      case "dmg-alerts":
        return <DMGAlertSystem />;
      case "grapevine-jobs":
        return <GrapevineJobs />;
      case "grapevine-creators":
        return (
          <Users
            key="creators"
            signupSource="grapevine_creator"
            match="include"
            title="Grapevine Creators"
          />
        );
      default:
        return <NotFoundView />;
    }
  };

  return (
    <div className="flex flex-col h-screen w-full bg-background">
      {/* Topbar - Fixed at the top */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <Topbar
          userName={super_admin?.name || user?.name || 'Admin'}
          userAvatar={super_admin?.image || user?.image}
          onLogout={handleLogOut}
          topBarColor="#334238"
          onNavigate={setContainer}
        />
      </div>

      {/* Content area - Below Topbar with proper spacing */}
      <SidebarProvider defaultOpen={true}>
        <style>{`
          .sidebar-with-topbar [class*="fixed"][class*="inset-y-0"] {
            top: 3rem !important;
            bottom: auto !important;
            height: calc(100vh - 3rem) !important;
          }
        `}</style>
        <div className="flex flex-1 pt-12 overflow-hidden sidebar-with-topbar">
          {/* Sidebar */}
          <div className="flex-none h-full bg-[#F7F4F2]">
            <Sidebar
              collapsible="icon"
              className="border-r border-sidebar-border transition-all duration-300 ease-in-out"
              style={{ backgroundColor: '#F7F4F2' }}
            >
            <SidebarContentComponent
              channels={channels}
              container={container}
              selectedOption={selectedOption}
              setContainer={setContainer}
              setSelectedOption={setSelectedOption}
              superAdmin={superAdmin}
              super_admin={super_admin}
            />
          </Sidebar>
          </div>

          {/* Main content area */}
          <main className="flex-1 overflow-auto">
            {renderContent()}
          </main>
        </div>
      </SidebarProvider>
    </div>
  );
};

export default Home;
